import React, { useState, useRef, useEffect, useContext } from 'react';
import './RemoteParkingPrologue.scss';
import VideoSimple from '@/v2/components/media/video/VideoSimple';
import Header from '@/v2/components/application/Header';
import TimelineText from '@/v2/components/atoms/TimelineText';
import TouchIndicator from '@/v2/components/atoms/Icon/TouchIndicator';
import TapDark from '@/v2/components/atoms/Icon/TapDark';
import TextBottom from '@/v2/components/atoms/TextBottom';
import TimeController from '@/v2/components/controllers/TimeController';
import Fade from '@/v2/components/atoms/Fade';
import { AnalyticsContext } from '@/v2/components/application/Analytics';
import Disclaimer from '@/v2/components/layouts/Disclaimer';
import { DriverAssistContext } from '@/v2/state/providers';
import { MomentStateContext } from '@/v2/state/momentState';
import { timeout, saveTimeout, clearTimeouts } from '@/v2/components/utils/Timeout';

const STATE = {
    none: 'none',
    cta: 'cta',
    video: 'video',
    fade: 'fade',
    end: 'end',
};

const FADE_TIME = 2800;

const videoReady = (controller, videoController, onReady) => {
    // eslint-disable-next-line no-param-reassign
    videoController.current = controller;
    onReady();
};

const stateVideo = (videoController, timeController, setTime, timeline, cb) => {
    videoController.current.Play();

    // eslint-disable-next-line no-param-reassign
    timeController.current = new TimeController(setTime);
    saveTimeout(timeController.current.AddTimeoutFromTimeline(timeline, () => cb()));
};

const getIntoTheCarTrackEvent = (trackEvent) => {
    const trackingObject = {
        vcStep: 'driverAssist',
        vcClick: 'hotspot:get into the car',
    };

    trackEvent('vcClick', trackingObject);
};

export default ({ isActive, data, onFinished, onReady }) => {
    const [state, setState] = useState(STATE.none);
    const videoController = useRef(null);
    const timeController = useRef(null);
    const [hideTouchIcon, setHideTouchIcon] = useState(false);
    const [animate, setAnimate] = useState(null);
    const [stopped, setStopped] = useState(true);
    const { trackEvent } = useContext(AnalyticsContext);
    const [{ disclaimer }] = useContext(DriverAssistContext);
    const { dispatch: momentDispatch } = useContext(MomentStateContext);

    const time = useRef(TimelineText.Time());

    const stateFade = (cb) => {
        timeout(() => cb(), FADE_TIME);
    };

    useEffect(() => () => clearTimeouts(), []);

    useEffect(() => {
        if (isActive) {
            setState(STATE.cta);
            timeout(() => {
                setStopped(false);
            }, 700);
        }
    }, [isActive]);

    useEffect(() => {
        if (isActive && videoController && videoController.current) {
            videoController.current.ProgressDispatch(momentDispatch);
        }
    }, [isActive, videoController.current]);

    useEffect(() => {
        switch (state) {
            case STATE.video:
                setTimeout(() => {
                    stateVideo(videoController, timeController, time.current.Set, data.text.timeline, () => setState(STATE.fade));
                }, 800);
                break;
            case STATE.fade:
                stateFade(() => setState(STATE.end));
                break;
            case STATE.end:
                onFinished();
                break;
            default:
        }
    }, [state]);

    const options = {
        bufferBeforePlay: true,
        onReady: (controller) => videoReady(controller, videoController, onReady),
        autoPlay: false,
    };

    const touchIndicator = !hideTouchIcon ? (
        <div
            className="RemoteParkingPrologue__touchIcon"
            onClick={() => {
                setHideTouchIcon(true);
                setAnimate('out');
                setState(STATE.video);
                getIntoTheCarTrackEvent(trackEvent);
            }}
            onKeyDown={onFinished}
            role="button"
            tabIndex="0"
        >
            <TouchIndicator />
        </div>
    ) : (
        ''
    );

    const disclaimerContent = disclaimer ? <Disclaimer data={disclaimer} dark /> : '';

    return (
        <div className="RemoteParkingPrologue">
            <VideoSimple src={data.video} options={options} />
            <Header show hideLogo dark />
            <TimelineText data={data.text.timeline} time={time.current} background="dark" color="black" />
            {touchIndicator}
            <div className="RemoteParkingPrologue__ctaTapText">
                <TextBottom background="dark" animate={state === STATE.cta ? 'in' : animate} color="black" component="h4">
                    {data.text.cta.tap}
                    <i className="icon">
                        <TapDark loop={false} isstopped={stopped} ispaused={stopped} />
                    </i>
                </TextBottom>
            </div>
            <Fade show={state === STATE.fade || state === STATE.end} light cover />
            {disclaimerContent}
        </div>
    );
};
